// ============================================
// SUPPLEMENT SELECTION DIALOG - RESPONSIVE
// Modal pour sélectionner les suppléments
// 100% responsive: mobile, tablette, desktop
// ============================================

import React, { useState, useMemo } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Plus, X, Search, Check, ChevronLeft } from 'lucide-react';

const SupplementSelectionDialog = ({
    open,
    onOpenChange,
    supplements = [],
    selectedSupplements = [],
    onAddSupplement,
    onRemoveSupplement,
    onConfirm,
    productName = '',
    currentTotal = 0,
    allowedSupplementIds = null, // Liste des IDs de suppléments autorisés pour ce produit (null = tous)
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    
    // Filtrer les suppléments par produit (si allowedSupplementIds défini)
    const productSupplements = useMemo(() => {
        if (!allowedSupplementIds || allowedSupplementIds.length === 0) {
            // Si pas de restriction, montrer tous les suppléments actifs
            return supplements.filter(s => s.is_active !== false);
        }
        // Sinon, filtrer par IDs autorisés
        return supplements.filter(s => 
            allowedSupplementIds.includes(s.id) && s.is_active !== false
        );
    }, [supplements, allowedSupplementIds]);
    
    // Filtrer les suppléments par recherche (utilise productSupplements au lieu de supplements)
    const filteredSupplements = useMemo(() => {
        if (!searchQuery.trim()) return productSupplements;
        const query = searchQuery.toLowerCase();
        return productSupplements.filter(sup => 
            sup.name.toLowerCase().includes(query)
        );
    }, [productSupplements, searchQuery]);
    
    // Grouper les suppléments par catégorie si disponible
    const groupedSupplements = useMemo(() => {
        const groups = {};
        filteredSupplements.forEach(sup => {
            const category = sup.category || sup.group || 'Autres';
            if (!groups[category]) groups[category] = [];
            groups[category].push(sup);
        });
        return groups;
    }, [filteredSupplements]);
    
    // Calculer le total des suppléments sélectionnés
    const supplementsTotal = useMemo(() => {
        return selectedSupplements.reduce((sum, sup) => sum + (sup.price || 0), 0);
    }, [selectedSupplements]);
    
    const isSelected = (supId) => selectedSupplements.some(s => s.id === supId);
    
    const handleToggle = (supplement) => {
        if (isSelected(supplement.id)) {
            onRemoveSupplement(supplement.id);
        } else {
            onAddSupplement(supplement);
        }
    };
    
    const handleClose = () => {
        setSearchQuery('');
        onOpenChange(false);
    };
    
    const handleConfirm = () => {
        setSearchQuery('');
        if (onConfirm) onConfirm();
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
            <DialogContent 
                hideCloseButton={true}
                className="
                    w-[95vw] max-w-lg 
                    h-[85vh] sm:h-auto sm:max-h-[85vh]
                    flex flex-col
                    p-0
                    rounded-t-2xl sm:rounded-2xl
                "
                data-testid="supplement-dialog"
            >
                {/* Header - Fixed */}
                <DialogHeader className="px-4 pt-4 pb-3 border-b shrink-0">
                    <div className="flex items-center justify-between">
                        <Button 
                            variant="ghost" 
                            size="icon"
                            className="sm:hidden h-10 w-10"
                            onClick={handleClose}
                            data-testid="supplement-close-mobile"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </Button>
                        <DialogTitle className="flex-1 text-center sm:text-left flex items-center gap-2 justify-center sm:justify-start">
                            <Plus className="w-5 h-5 text-emerald-500" />
                            <span className="text-base sm:text-lg">Suppléments</span>
                        </DialogTitle>
                        <Button 
                            variant="ghost" 
                            size="icon"
                            className="hidden sm:flex h-10 w-10 hover:bg-red-100 hover:text-red-600"
                            onClick={handleClose}
                            data-testid="supplement-close-desktop"
                        >
                            <X className="w-5 h-5" />
                        </Button>
                    </div>
                    
                    {productName && (
                        <p className="text-sm text-muted-foreground text-center sm:text-left mt-1">
                            Pour: <span className="font-medium text-foreground">{productName}</span>
                        </p>
                    )}
                    
                    {/* Barre de recherche - visible si >8 suppléments */}
                    {supplements.length > 8 && (
                        <div className="relative mt-3">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                type="text"
                                placeholder="Rechercher..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-9 h-11"
                                data-testid="supplement-search"
                            />
                        </div>
                    )}
                </DialogHeader>

                {/* Content - Scrollable */}
                <div className="flex-1 overflow-y-auto px-4 py-3">
                    {/* Suppléments sélectionnés */}
                    {selectedSupplements.length > 0 && (
                        <div className="mb-4 p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl">
                            <p className="text-xs font-medium text-emerald-700 dark:text-emerald-300 mb-2">
                                Sélectionnés ({selectedSupplements.length})
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {selectedSupplements.map(sup => (
                                    <Badge 
                                        key={sup.id}
                                        className="bg-emerald-600 hover:bg-emerald-700 cursor-pointer py-1.5 px-3 text-sm"
                                        onClick={() => onRemoveSupplement(sup.id)}
                                    >
                                        {sup.name}
                                        <span className="ml-1 opacity-75">+{(sup.price || 0).toLocaleString()}</span>
                                        <X className="w-3 h-3 ml-1" />
                                    </Badge>
                                ))}
                            </div>
                            <p className="text-sm font-bold text-emerald-700 dark:text-emerald-300 mt-2">
                                Total suppléments: +{supplementsTotal.toLocaleString()} F
                            </p>
                        </div>
                    )}
                    
                    {/* Liste des suppléments */}
                    {Object.keys(groupedSupplements).length > 1 ? (
                        // Affichage groupé
                        Object.entries(groupedSupplements).map(([category, categorySupplements]) => (
                            <div key={category} className="mb-4">
                                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 px-1">
                                    {category}
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {categorySupplements.map(sup => (
                                        <SupplementButton
                                            key={sup.id}
                                            supplement={sup}
                                            isSelected={isSelected(sup.id)}
                                            onToggle={() => handleToggle(sup)}
                                        />
                                    ))}
                                </div>
                            </div>
                        ))
                    ) : (
                        // Affichage simple - 2 colonnes max pour que les noms soient visibles
                        <div className="grid grid-cols-2 gap-3">
                            {filteredSupplements.map(sup => (
                                <SupplementButton
                                    key={sup.id}
                                    supplement={sup}
                                    isSelected={isSelected(sup.id)}
                                    onToggle={() => handleToggle(sup)}
                                />
                            ))}
                        </div>
                    )}
                    
                    {filteredSupplements.length === 0 && (
                        <div className="text-center py-8 text-muted-foreground">
                            {searchQuery ? (
                                <p>Aucun supplément trouvé pour "{searchQuery}"</p>
                            ) : productSupplements.length === 0 ? (
                                <div className="space-y-2">
                                    <p className="font-medium">Aucun supplément configuré</p>
                                    <p className="text-sm">Configurez les suppléments dans les paramètres du produit</p>
                                </div>
                            ) : (
                                <p>Aucun supplément disponible</p>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer - Fixed */}
                <DialogFooter className="px-4 py-3 border-t shrink-0 bg-background">
                    <div className="w-full flex flex-col sm:flex-row gap-2">
                        {/* Total mis à jour */}
                        {currentTotal > 0 && (
                            <div className="flex-1 flex items-center justify-center sm:justify-start text-sm">
                                <span className="text-muted-foreground">Nouveau total:</span>
                                <span className="ml-2 font-bold text-lg">
                                    {(currentTotal + supplementsTotal).toLocaleString()} F
                                </span>
                            </div>
                        )}
                        
                        <div className="flex gap-2 w-full sm:w-auto">
                            <Button 
                                variant="outline" 
                                onClick={handleClose}
                                className="flex-1 sm:flex-none h-12 sm:h-10"
                            >
                                Annuler
                            </Button>
                            <Button 
                                onClick={handleConfirm}
                                className="flex-1 sm:flex-none bg-emerald-600 hover:bg-emerald-700 h-12 sm:h-10"
                                data-testid="supplement-confirm-btn"
                            >
                                <Check className="w-4 h-4 mr-2" />
                                Valider
                                {selectedSupplements.length > 0 && (
                                    <Badge className="ml-2 bg-white text-emerald-700">
                                        {selectedSupplements.length}
                                    </Badge>
                                )}
                            </Button>
                        </div>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

// Composant bouton de supplément avec état de sélection très visible
const SupplementButton = ({ supplement, isSelected, onToggle }) => (
    <button
        onClick={onToggle}
        className={`
            w-full p-3 sm:p-4 rounded-xl border-2 transition-all duration-200
            flex flex-col items-center justify-center gap-2
            min-h-[80px] sm:min-h-[90px]
            touch-manipulation
            ${isSelected 
                ? 'border-emerald-500 bg-emerald-100 dark:bg-emerald-800/50 ring-2 ring-emerald-400 ring-offset-1 shadow-lg shadow-emerald-200 dark:shadow-emerald-900/50' 
                : 'border-gray-200 dark:border-zinc-700 hover:border-emerald-300 dark:hover:border-emerald-700 hover:bg-gray-50 dark:hover:bg-zinc-800'
            }
        `}
        data-testid={`supplement-btn-${supplement.id}`}
    >
        {/* Indicateur de sélection et nom */}
        <div className="flex items-center gap-2">
            {isSelected ? (
                <div className="shrink-0 w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" strokeWidth={3} />
                </div>
            ) : (
                <div className="shrink-0 w-6 h-6 rounded-full border-2 border-gray-300 dark:border-zinc-600 flex items-center justify-center">
                    <Plus className="w-3 h-3 text-gray-400" />
                </div>
            )}
            <span className={`font-medium text-center text-sm sm:text-base ${isSelected ? 'text-emerald-800 dark:text-emerald-200 font-semibold' : 'text-gray-700 dark:text-gray-300'}`}>
                {supplement.name || 'Supplément'}
            </span>
        </div>
        
        {/* Prix */}
        <span className={`font-bold text-sm sm:text-base px-3 py-1 rounded-lg ${
            isSelected 
                ? 'bg-emerald-500 text-white' 
                : 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30'
        }`}>
            +{(supplement.price || 0).toLocaleString()} F
        </span>
    </button>
);

export default SupplementSelectionDialog;
